import { AsyncPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  Signal,
  computed,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { FUTURES_MENU_ITEMS, MENU_ITEMS, SideNavMenuItem, socialLink } from "@constants";
import { FuturesModeService } from "@services";
import { TranslocoModule } from "@ngneat/transloco";
import type { Observable } from "rxjs";
import { Store, clearCurrentUser } from "@store";
import { DeviceService } from "src/app/core/service/device.service";
import { LanguageDropdownComponent } from "src/app/shared/components/language-dropdown/language-dropdown.component";
import { ButtonComponent } from "../button/button.component";
import { CommonService } from "../common.service";
import { ThemeSwitcherComponent } from "../theme-switcher/theme-switcher.component";
import { SideNavItemComponent } from "./side-nav-item/side-nav-item.component";
import { SvgIconComponent } from "../social-icons/svg-icon/svg-icon.component";

@Component({
  selector: "app-side-nav",
  templateUrl: "./side-nav.component.html",
  styleUrls: ["./side-nav.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    LanguageDropdownComponent,
    SideNavItemComponent,
    AsyncPipe,
    ThemeSwitcherComponent,
    ButtonComponent,
    SvgIconComponent,
  ],
})
export class SideNavComponent implements OnInit {
  public readonly social = socialLink;
  public isCollapsed: boolean = false;
  public expanded: boolean[] = [false, false, false];
  public navigationLoader: boolean = false;
  public isMobile$: Observable<boolean> = this._deviceService.isBelow(991);

  public readonly menuItems: Signal<SideNavMenuItem[]> = computed(() =>
    this._futuresModeService.isFuturesRoute() ? FUTURES_MENU_ITEMS : MENU_ITEMS,
  );

  public readonly logoNavigateTo: Signal<string> = computed(() =>
    this._futuresModeService.isFuturesRoute() ? "futures/dashboard" : "dashboard",
  );

  public readonly newChallengeNavigateTo: Signal<string> = computed(() =>
    this._futuresModeService.isFuturesRoute()
      ? "futures/new-challenge/get-funded"
      : "new-challenge/get-funded",
  );

  constructor(
    private _router: Router,
    public commonService: CommonService,
    private _store: Store,
    private _deviceService: DeviceService,
    private _cdr: ChangeDetectorRef,
    private _destroyRef: DestroyRef,
    private _futuresModeService: FuturesModeService,
  ) {}

  public ngOnInit(): void {
    this.commonService.sidebar.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.isCollapsed = !this.isCollapsed;
      this._cdr.markForCheck();
    });
  }

  public logout(): void {
    localStorage.clear();
    this._router.navigate(["/auth/login"]);
    this._store.dispatch(clearCurrentUser());
  }

  public expandMenu(index: number): void {
    this.expanded[index] = !this.expanded[index];
  }

  public navigateToUrl(url: string): void {
    this.navigationLoader = true;

    this._router.navigate([url]).finally(() => {
      this.navigationLoader = false;
    });
  }
}
